import dayjs from 'dayjs';
import { Button, Grid } from '@mui/material';
import { useState } from 'react';
import CodeIcon from '@mui/icons-material/Code';
import Model from '../models/model';
import consts from '../consts';
import connection from '../services/Connection';
import strings from '../Globalization';
import XmlEditorDialog from '../contexts/CycleInfo/XmlEditorDialog';
import LoadingDialog from '../components/common/LoadingDialog';
import util from '../util';
import { CycleHistory } from './CycleTable';

const sterisAdminRegex = /steris\s*admin/i;

/**
 * Renders a button to view XML data with an XML editor dialog
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.row - Row data containing cycle information
 * @param {string} props.row.id - Cycle ID
 * @param {string} props.row.EquipmentSerialNumber - Equipment serial number
 * @param {string} props.row.EquipmentType - Type of equipment
 * @returns {JSX.Element} ViewXML component
 */
const ViewXML = ({ row }) => {
	const {
		id: cycleId = '',
		EquipmentSerialNumber: serialNumber = '',
		EquipmentType: equipmentType = '',
		StorageFileName: fileName = ''
	} = row;
	const [state, setState] = useState({
		xmlEditorOpen: false,
		xmlEditorValue: '',
		xmlEditorTitle: '',
		xmlFileName: fileName,
		loading: false
	});

	/**
     * Closes the XML editor dialog and resets its state
     */
	const onCloseXmlEditorDialog = () => {
		setState((prevState) => ({ ...prevState, xmlEditorOpen: false, xmlEditorValue: '', xmlEditorTitle: '' }))
	}

	/**
     * Fetches and displays XML data for the selected cycle
     * @async
     * @throws {Error} When XML preview request fails
     */
	const onViewXML = async () => {
		const xmlEditorTitle = `${strings.XMLName} ${equipmentType}/${serialNumber}`;
		setState({ loading: true });
		const formData = new FormData();
		formData.append('action', 'otherAction');
		formData.append('otherAction', 'PreviewXML');
		formData.append('cycleId', cycleId);
		formData.append('cycleType', 'atlas');
		const response = await fetch(connection.CycleList, {
			method: 'POST',
			body: formData
		});
		const contentType = response.headers.get('Content-Type');
		let newState = {};
		if(contentType && contentType.includes('text/xml')) {
			const fileData = await response.text();
			newState = { ...state, xmlEditorOpen: true, xmlEditorValue: fileData, xmlEditorTitle, loading: false };
		} else {
			const responseData = await response.json();
			newState = { ...state, loading: false };
			util.showError(strings[responseData.err] || strings.ViewXMLError, strings.Error, 'error');
		}
		setState(newState);
	}
	return (
		<>
			<Button variant='contained' color="primary" size="medium" startIcon={< CodeIcon />} onClick={onViewXML} >
				{strings.ViewXML}
			</Button>
			<Grid size={12}>
				<XmlEditorDialog {...state} onCloseXmlEditorDialog={onCloseXmlEditorDialog} />
			</Grid>
			<Grid size={12}>
				<LoadingDialog isOpen={state.loading} />
			</Grid>
		</>
	)
}

const model = new Model({
	name: 'AtlasCycleList',
	idProperty: 'id',
	disableAdd: true,
	defaultSort: 'CycleDate DESC',
	disableDelete: true,
	gridRefreshInterval: 300000,
	showGridRefreshBtn: true,
	combos: [
		{ type: 'Location', loaded: true },
	],
	gridToolbarCustomActions: [
		{
			Component: CycleHistory
		}
	],
	columns: [
		{ field: 'EquipmentName', header: 'EquipmentName', width: 150 },
		{ field: 'EquipmentSerialNumber', header: 'SerialNumber', width: 150 },
		{ field: 'CycleCount', header: 'CycleCount', width: 150 },
		{ field: 'Side', header: 'Side', width: 150 },
		{ field: 'CycleDate', header: 'CycleDateTime', width: 150, type: 'dateTime' },
		{ field: 'FromType', header: 'FromType', width: 150 },
		{ field: 'FromName', header: 'FromName', width: 150 },
		{ field: 'ToType', header: 'ToType', width: 150 },
		{ field: 'ToName', header: 'ToName', width: 150 },
		{ field: 'LocationCycleCount', header: 'LocationCycleCount', width: 200 },
		{ field: 'LocationFileName', header: 'LocationFileName', width: 300 },
		{ field: 'RackId', header: 'RackId', width: 150 },
		{ field: 'LoadType', header: 'LoadType', width: 150 },
		{
			field: 'LocationId',
			header: 'Location',
			type: 'string',
			lookup: 'Location',
			hide: true,
			headerFilter: true,
			standaloneHeaderFilter: true,
			isNotFormfield: true,
			headerFilterWrapperProps: {
				xs: 6,
				container: true
			}
		},
		{
			field: 'StartDate',
			header: 'StartDate',
			type: 'date',
			format: consts.MomentFormat.MonthDateYear,
			maxDate: ({ formik }) => (formik.values.endDate ? dayjs(formik.values.endDate) : dayjs()),
			hide: true,
			headerFilter: true,
			standaloneHeaderFilter: true,
			isNotFormfield: true,
			headerFilterWrapperProps: {
				xs: 6,
				container: true
			}
		},
		{
			field: 'EndDate',
			header: 'EndDate',
			type: 'date',
			format: consts.MomentFormat.MonthDateYear,
			minDate: ({ formik }) => (formik.values.startDate ? dayjs(formik.values.startDate) : undefined),
			maxDate: () => dayjs(),
			headerFilter: true,
			standaloneHeaderFilter: true,
			isNotFormfield: true,
			headerFilterWrapperProps: {
				xs: 6,
				container: true
			}
		},
		{
			field: 'ViewXML',
			header: 'ViewXML',
			width: 160,
			excludeOnGrid: ({ auth }) => { 
				const { tags: { RoleName: roleName = '' } = {} } = auth;
				return !sterisAdminRegex.test(roleName);
			},
			renderCell: ViewXML,
			inputWhen: ({ auth = {} }) => {
				const { tags: { RoleName: roleName = '' } = {} } = auth;
				return !consts.roleArrayForViewXML.includes(roleName);
			}
		}
	]
});

export const AtlasCycleDetail = model;
export default model.createFormBase();